import { useState, useEffect } from 'react';
import { getPlace, deletePlace, updatePlace } from '../../services/api';
import { PlaceHeader } from './PlaceHeader';
import { PlacePhotoManager } from './PlacePhotoManager';
import { VisitList } from './VisitList';
import { AddVisitForm } from './AddVisitForm';
import { EditVisitForm } from './EditVisitForm';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { ErrorBanner } from '../UI/ErrorBanner';
import type { Place, PlaceDetail, NominatimResult } from '../../types';

type Mode = 'view' | 'add' | 'edit';

interface PlacePanelProps {
  selectedPlace: Place | null;
  pendingNominatim: NominatimResult | null;
  onClose: () => void;
  onVisitAdded: () => void;
}

export function PlacePanel({ selectedPlace, pendingNominatim, onClose, onVisitAdded }: PlacePanelProps) {
  const [detail, setDetail] = useState<PlaceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isOpen = !!selectedPlace || !!pendingNominatim;

  useEffect(() => {
    if (!selectedPlace) {
      setDetail(null);
      setMode(pendingNominatim ? 'add' : 'view');
      return;
    }
    setMode('view');
    setLoading(true);
    setError(null);
    setDeleting(false);
    setDeleteError(null);
    getPlace(selectedPlace.PlaceId)
      .then(setDetail)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedPlace, pendingNominatim]);

  const handleVisitAdded = () => {
    onVisitAdded();
    onClose();
  };

  const handleDelete = async () => {
    if (!selectedPlace) return;
    if (!window.confirm('Delete visit to this place?')) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePlace(selectedPlace.PlaceId);
      setDeleting(false);
      onVisitAdded();
      onClose();
    } catch (err) {
      setDeleteError((err as Error).message);
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: 360,
        maxWidth: '90vw',
        background: '#fff',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        zIndex: 20,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-end',
          margin: 8,
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid #e0e0e0',
          borderRadius: '50%',
          width: 32,
          height: 32,
          cursor: 'pointer',
          fontSize: 18,
          zIndex: 1,
        }}
      >
        ×
      </button>

      {mode === 'add' && pendingNominatim ? (
        <AddVisitForm
          nominatimResult={pendingNominatim}
          onSuccess={handleVisitAdded}
          onCancel={onClose}
        />
      ) : mode === 'edit' && detail && selectedPlace ? (
        <EditVisitForm
          placeId={selectedPlace.PlaceId}
          placeName={detail.PlaceName}
          visit={detail.visits[0]}
          onSuccess={handleVisitAdded}
          onCancel={() => setMode('view')}
        />
      ) : loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      ) : detail ? (
        <>
          <PlaceHeader place={detail} onNameChanged={async (newName) => {
            if (!detail.visits[0]) return;
            const v = detail.visits[0];
            try {
              await updatePlace(detail.PlaceId, {
                visitType: v.VisitType, visitDate: v.VisitDate ?? '', notes: v.Notes ?? '',
                userImageBlobURL: v.UserImageBlobURL, soloTraveller: v.SoloTraveller,
                placeName: newName,
              });
              setDetail((d) => d ? { ...d, PlaceName: newName } : d);
              onVisitAdded(); // refresh sidebar
            } catch {}
          }} />
          <PlacePhotoManager
            placeId={detail.PlaceId}
            photoURLs={[detail.PhotoURL1, detail.PhotoURL2, detail.PhotoURL3, detail.PhotoURL4, detail.PhotoURL5]}
            onPhotosChanged={(photos) => setDetail((d) => d ? { ...d, ...photos } : d)}
          />
          <VisitList visits={detail.visits} />
          {deleteError && (
            <div style={{ padding: '0 16px 8px' }}>
              <ErrorBanner message={deleteError} onDismiss={() => setDeleteError(null)} />
            </div>
          )}
          <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {detail.visits.length === 0 && (
              <button
                onClick={() => setMode('add')}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#4CAF50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                + Add Visit
              </button>
            )}
            {detail.visits.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setMode('edit')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#fff',
                    color: '#2196F3',
                    border: '1px solid #2196F3',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Edit visit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: deleting ? '#e0e0e0' : '#fff',
                    color: deleting ? '#9e9e9e' : '#d32f2f',
                    border: '1px solid #d32f2f',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: deleting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
